// Collects single-id lookups made within a short window into batched requests.
// Feed and market cards each ask for their own ordinal's state on mount; a batch
// endpoint already takes many ids, so a page of cards can ask once.

export function createIdBatcher<T>(opts: {
    fetchMany: (ids: string[]) => Promise<Record<string, T>>
    windowMs?: number
    maxIds?: number
    schedule?: (flush: () => void) => void
}) {
    const windowMs = opts.windowMs ?? 20
    const maxIds = opts.maxIds ?? 100
    const schedule = opts.schedule ?? (flush => setTimeout(flush, windowMs))
    type Waiter = { resolve: (value: T | null) => void; reject: (error: unknown) => void }
    let pending = new Map<string, Waiter[]>()
    let scheduled = false

    const run = async (batch: Map<string, Waiter[]>) => {
        try {
            const results = await opts.fetchMany(Array.from(batch.keys()))
            batch.forEach((waiters, id) => waiters.forEach(w => w.resolve(results?.[id] ?? null)))
        } catch (error) {
            batch.forEach(waiters => waiters.forEach(w => w.reject(error)))
        }
    }

    const flush = () => {
        scheduled = false
        const all = pending
        pending = new Map()
        const ids = Array.from(all.keys())
        for (let i = 0; i < ids.length; i += maxIds) {
            void run(new Map(ids.slice(i, i + maxIds).map(id => [id, all.get(id)!])))
        }
    }

    return (id: string): Promise<T | null> =>
        new Promise((resolve, reject) => {
            const waiters = pending.get(id)
            if (waiters) waiters.push({ resolve, reject })
            else pending.set(id, [{ resolve, reject }])
            if (!scheduled) {
                scheduled = true
                schedule(flush)
            }
        })
}
